import { v4 as uuidv4 } from "uuid";
import { uploadFile, importFileData } from "../services/fileDataService";
import NotificationService from "../services/notificationService";
import { getUserIdFromLocalStorage, logFileProcess } from "../API_Endpoints/APIEndpoints";
import { businessunitName } from "../App_Settings/app_settings";
/** Column Definitions */

export const columns = [
  {
    field: "fileName",

    headerName: "File Name",

    editable: true,
  },

  {
    field: "uploadType",

    headerName: "Upload Type",

    editable: true,
  },

  {
    field: "businessUnit",

    headerName: businessunitName,

    editable: true,
  },

  {
    field: "uploadedBy",

    headerName: "Uploaded By",

    editable: true,
  },

  {
    field: "uploadedDate",

    headerName: "Uploaded Date",

    editable: true,
  },

  {
    field: "processStatus",

    headerName: "Status",

    editable: true,
  },

  {
    field: "processQueue",

    headerName: "",

    sortable: false,
  },
];

export const getFormTypeDisplayName = (formType: any) => {
  if (!formType) return "";

  switch (formType.toLowerCase()) {
    case "w8imy":
      return "W-8IMY";

    case "w8bene":
      return "W-8BEN-E";

    case "w9":
      return "W-9";

    case "w8ben":
      return "W-8BEN";

    case "w8exp":
      return "W-8EXP";

    case "w8eci":
      return "W-8ECI";

    default:
      return formType; // Return the original if not matched
  }
};
/**
 * Map document types to upload type values
 * @param documentType - The document type string from API
 * @returns The corresponding upload type number (1-11) or null
 */

export const mapDocumentTypeToUploadType = (
  documentType: string | null | undefined
): number | null => {
  if (!documentType) return null;

  // Normalize the document type to uppercase for consistent matching
  const normalizedType = documentType.toString().toUpperCase().trim();

  const typeMap: Record<string, number> = {
    WHS: 1,
    "WITHHOLDING STATEMENT": 1,
    "W-9": 2,
    W9: 2,
    "W-8BEN": 3,
    W8BEN: 3,
    "W-8BEN-E": 4,
    W8BENE: 4,
    "W-8IMY": 5,
    W8IMY: 5,
    "W-8ECI": 6,
    W8ECI: 6,
    "W-8EXP": 7,
    W8EXP: 7,
    "8233": 8,
    // Self-Cert Individual aliases
    "SELFCERT-IND": 9,
    "SELFCERT-ENT": 10,
    "Pack With Forms": 11,
    "PACK WITH FORMS": 11,
  };

  return typeMap[normalizedType] || null;
};

/**
 * Get display name for upload type (shows specific form types)
 * @param uploadType - The upload type number
 * @returns User-friendly display name for the upload type
 */
export const getUploadTypeDisplayName = (uploadType: number | null | undefined): string => {
  if (!uploadType) return "";

  switch (uploadType) {
    case 1:
      return "Withholding Statement";
    case 2:
      return "W-9";
    case 3:
      return "W-8BEN";
    case 4:
      return "W-8BEN-E";
    case 5:
      return "W-8IMY";
    case 6:
      return "W-8ECI";
    case 7:
      return "W-8EXP";
    case 8:
      return "8233";
    case 9:
      return "Self-Cert Individual";
    case 10:
      return "Self-Cert Entity";
    case 11:
      return "Pack With Forms";
    default:
      // return `Type ${uploadType}`;
      return `Withholding Statement`;
  }
};

/**
 * Configuration options for handleFileUpload
 */
export interface FileUploadConfig {
  // Mode: 'workload' allows all document types, 'bulkForm' only allows single forms (uploadType 2-10)
  mode: "workload" | "bulkForm";

  // User information
  userId: string;
  userName: string;

  // Business unit configuration
  businessUnitsList: Array<{ id: string; name: string }>;
  defaultBusinessunit: string;

  // State management callbacks
  setIsParallelUploading: (value: boolean) => void;
  setUploadProgress: React.Dispatch<React.SetStateAction<Record<number, any>>>;

  // Data management
  rows: any[];
  setRows: (rows: any[]) => void;

  // Notification management
  notifications: any[];
  handleNotificationList: (notifications: any[]) => void;

  // Optional: Custom notification function for bulk form import
  addNotification?: (type: string, message: string, fileName: string) => void;

  // Callbacks
  getAllFiles: () => Promise<void>;
  storeReports: (reportData: any) => Promise<any>;

  // Display helper
  getUploadTypeDisplayName: (uploadType: number) => string;
}

/**
 * Centralized file upload handler for both Workload File Import and Bulk Form Import
 * Processes multiple files in parallel with progress tracking
 */
export const handleFileUpload = async (
  event: React.ChangeEvent<HTMLInputElement>,
  config: FileUploadConfig
) => {
  const fileInput = event.target;

  // Stop if no files selected
  if (!fileInput.files || fileInput.files.length === 0) {
    return;
  }

  try {
    config.setIsParallelUploading(true);

    const files = Array.from(fileInput.files);
    const allowedExtensions = ["pdf", "xlsx", "docx"];
    const newRows = [...config.rows];

    // Initialize progress tracking
    const initialProgress: Record<number, any> = {};
    files.forEach((file, index) => {
      initialProgress[index] = {
        fileName: file.name,
        status: "pending",
        message: "Waiting to start...",
      };
    });
    config.setUploadProgress(initialProgress);

    // Process all files in parallel
    const fileProcessingPromises = files.map(async (file, index) => {
      const fileName = file.name;
      const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

      // Update progress - starting
      config.setUploadProgress((prev) => ({
        ...prev,
        [index]: { ...prev[index], status: "processing", message: "Starting upload..." },
      }));

      // Validate file type
      if (!allowedExtensions.includes(fileExtension)) {
        console.log(`❌ Invalid file type: ${fileName}`);
        config.setUploadProgress((prev) => ({
          ...prev,
          [index]: { ...prev[index], status: "failed", message: "Invalid file type" },
        }));

        // Add failure notification
        const errorMessage = "Unsupported file type. Only PDF, XLSX, or DOCX files are allowed.";
        if (config.addNotification) {
          config.addNotification("failed", errorMessage, fileName);
        } else {
          const errorNotification = NotificationService.fileUploadError(fileName, errorMessage);
          config.handleNotificationList([...config.notifications, errorNotification]);
        }

        return {
          success: false,
          fileName: fileName,
          error: errorMessage,
        };
      }

      try {
        const fileId = uuidv4();
        const formData = new FormData();
        formData.append("file", file);
        formData.append("filename", fileId);
        formData.append("original_filename", fileName);
        formData.append("organizationId", getUserIdFromLocalStorage().organizationId);
        formData.append("userId", getUserIdFromLocalStorage().userId);

        // Auto-assign first available business unit from shared context
        const firstBusinessUnit =
          config.businessUnitsList.find((bu) => bu.id !== "all" && bu.name)?.name ||
          config.defaultBusinessunit ||
          "Demo";
        formData.append("businessUnit", firstBusinessUnit);

        // Update progress - uploading
        config.setUploadProgress((prev) => ({
          ...prev,
          [index]: { ...prev[index], status: "processing", message: "Uploading file..." },
        }));

        // Upload file using centralized service
        const uploadResult = await uploadFile(formData);

        // Update progress - processing
        config.setUploadProgress((prev) => ({
          ...prev,
          [index]: { ...prev[index], status: "processing", message: "Processing document..." },
        }));

        // Get the response data with document classification
        const uploadResponseData = uploadResult.data;

        // Extract document type and map to uploadType
        const documentType = uploadResponseData.document_type;
        const uploadType = mapDocumentTypeToUploadType(documentType);

        // Bulk Form Import mode: Only allow single forms (uploadType 2-10)
        if (config.mode === "bulkForm") {
          if (!uploadType || uploadType === 1 || uploadType === 11) {
            let errorMessage = "";
            if (!uploadType) {
              errorMessage = `Document type "${documentType || "Unknown"}" could not be classified. Only single forms (W-8, W-9, Self-cert, Form 8233) are allowed in Bulk Form Import.`;
            } else if (uploadType === 1 || uploadType === 11) {
              errorMessage = `Document type "${documentType}" is not supported in Bulk Form Import. Only single forms (W-8, W-9, Self-cert, Form 8233) are allowed. Please use regular File Import for Withholding Statement and Pack with Forms.`;
            }
            throw new Error(errorMessage);
          }
        }

        // Workload mode: Warn if document type is not recognized
        if (config.mode === "workload") {
          if (!uploadType && documentType) {
            console.warn(
              `⚠️ Unknown document type: ${documentType}. UploadType will be set to null.`
            );
          } else if (!documentType) {
            console.warn(`⚠️ No document type returned from API. UploadType will be set to null.`);
          }
        }

        // Prepare new row for file import
        const newRow = {
          fileImportId: fileId,
          userId: config.userId,
          fileName: file.name,
          filePath: uploadResponseData.pdf_filename || "",
          prcoessIdentifier: "AZURE-AI",
          isProcessed: false,
          businessUnit: firstBusinessUnit,
          uploadedBy: config.userName,
          uploadedDate: new Date().toISOString(),
          reviewedBy: null,
          reviewedDate: null,
          numberOfFields: null,
          confidenceLevel: null,
          approvedBy: null,
          approvedDate: null,
          emailedTo: null,
          emailedDate: null,
          isInvestment: false,
          isWithholding: true,
          numberOfRows: null,
          numberOfTiers: null,
          uploadType: uploadType,
          status: null,
          rejectedBy: null,
          rejectedDate: null,
          accuracyPercentage: null,
          rejectionReason: "",
          notes: documentType ? `Document Type: ${documentType}` : "",
          totalPages:
            uploadResponseData?.reportdata?.totalPages ||
            uploadResponseData?.statistics?.total_pages ||
            1,
          totalForms:
            uploadType === 1
              ? 0
              : uploadResponseData?.reportdata?.totalForms ||
                uploadResponseData?.statistics?.total_forms ||
                1,
        };

        // Import file data using centralized service
        const importResult = await importFileData([newRow]);
        const data = importResult.data;

        // Store report data if available from API response
        if (uploadResponseData && uploadResponseData.reportdata) {
          try {
            const reportResult = await config.storeReports(uploadResponseData.reportdata);
            if (reportResult?.success) {
              console.log(`✅ Upload report stored successfully (${config.mode} mode)`);
            } else {
              console.warn(
                `⚠️ Upload report storage failed (${config.mode} mode):`,
                reportResult?.error
              );
            }
          } catch (err) {
            console.error(`❌ Failed to store upload report (${config.mode} mode):`, err);
            // Don't let report storage failure break the upload process
          }
        }

        // Log file process
        logFileProcess({
          fileImportId: fileId,
          fileName: file.name,
          uploadDate: new Date(),
          extractedDate: null,
          approvedDate: null,
          rejectedDate: null,
          exportedDate: null,
          deletedDate: null,
        });

        // Update progress - completed
        config.setUploadProgress((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            status: "completed",
            message: "Upload completed successfully",
          },
        }));

        // Add success notification
        const displayName = uploadType
          ? config.getUploadTypeDisplayName(uploadType)
          : "Unknown type";
        const successMessage = `File uploaded and processed successfully as ${displayName}`;
        if (config.addNotification) {
          config.addNotification("success", successMessage, fileName);
        } else {
          const uploadNotification = NotificationService.fileUploadSuccess(
            fileName,
            fileId,
            documentType ? `Document Type: ${documentType}` : undefined
          );
          config.handleNotificationList([...config.notifications, uploadNotification]);
        }

        return {
          success: true,
          fileName: fileName,
          fileId: fileId,
          documentType: documentType,
          uploadType: uploadType,
          data: data[0],
        };
      } catch (fileError: any) {
        console.error(`❌ Error processing file ${fileName} in parallel:`, fileError);

        // Update progress - failed
        config.setUploadProgress((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            status: "failed",
            message: fileError.message || "Upload failed",
          },
        }));

        // Add error notification
        const errorMessage = fileError.message || "Unknown error occurred";
        if (config.addNotification) {
          config.addNotification("failed", `File upload failed: ${errorMessage}`, fileName);
        } else {
          const errorNotification = NotificationService.fileUploadError(fileName, errorMessage);
          config.handleNotificationList([...config.notifications, errorNotification]);
        }

        return {
          success: false,
          fileName: fileName,
          error: errorMessage,
        };
      }
    });

    // Wait for all file processing to complete
    const results = await Promise.all(fileProcessingPromises);

    // Separate successful and failed uploads
    const successfulUploads = results.filter((result) => result.success);
    // const failedUploads = results.filter((result) => !result.success);

    // Update rows with successful uploads
    successfulUploads.forEach((upload) => {
      if (upload.data) {
        newRows.splice(0, 0, upload.data);
      }
    });

    config.setRows(newRows);
    await config.getAllFiles();

    // Reset file input
    fileInput.value = "";
    config.setIsParallelUploading(false);
    config.setUploadProgress({});
    // Wait 1 second to show all files as COMPLETED, then show alert and close modal
    // setTimeout(() => {
    //   if (successfulUploads.length > 0) {
    //     if (failedUploads.length === 0) {
    //       // Create detailed success message with document types
    //       const successDetails = successfulUploads
    //         .map(
    //           (f: any) =>
    //             `• ${f.fileName} (${config.getUploadTypeDisplayName(f.uploadType) || "Unknown type"})`
    //         )
    //         .join("\n");

    // alert(`Successfully uploaded ${successfulUploads.length} file(s):\n\n${successDetails}`);
    //     } else {
    // alert(
    //   `Upload completed: ${successfulUploads.length} successful, ${failedUploads.length} failed.`
    // );
    //     }
    //   }

    //   // Close modal after alert
    //   config.setIsParallelUploading(false);
    //   config.setUploadProgress({});
    // }, 1000);
  } catch (error: any) {
    console.error("❌ File upload error:", error.message);
    alert(error.message || "An error occurred during file upload");
  }
};

//   const formatElapsedTime = (seconds) => {
//     const hours = Math.floor(seconds / 3600);

//     const minutes = Math.floor((seconds % 3600) / 60);

//     const secs = seconds % 60;

//     if (hours > 0) {
//       return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
//     } else {
//       return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
//     }
//   };
